/**
 * Protected route wrapper that enforces authentication and role-based access.
 *
 * Behavior:
 * - Shows a loading spinner while the initial auth check is in progress
 * - Redirects unauthenticated users to /login (preserving intended destination)
 * - Redirects users without the required role to their correct dashboard with access denied notification
 * - Renders children/outlet when access is granted
 */

import { Box, CircularProgress } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { UserRole } from 'shared';
import { useAuth } from '../../contexts/AuthContext';

export interface ProtectedRouteProps {
  /** Roles allowed to access this route. If empty, any authenticated user can access. */
  allowedRoles?: UserRole[];
  /** Optional children to render instead of Outlet */
  children?: React.ReactNode;
}

/**
 * Determines the correct dashboard path for a user based on their role.
 */
function getDashboardPath(role: UserRole): string {
  if (role === UserRole.Admin) {
    return '/admin';
  }
  return '/dashboard';
}

export default function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading spinner during initial auth check
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check role-based access if allowedRoles is specified
  if (allowedRoles && allowedRoles.length > 0) {
    const hasAccess = allowedRoles.includes(user.role);

    if (!hasAccess) {
      // Redirect to the user's correct dashboard with access denied notification
      const correctPath = getDashboardPath(user.role);
      return (
        <Navigate
          to={correctPath}
          state={{ accessDenied: true, attemptedPath: location.pathname }}
          replace
        />
      );
    }
  }

  // Access granted — render children or outlet
  return children ? <>{children}</> : <Outlet />;
}

/**
 * Convenience wrapper for admin-only routes.
 */
export function AdminRoute({ children }: { children?: React.ReactNode }) {
  return (
    <ProtectedRoute allowedRoles={[UserRole.Admin]}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Convenience wrapper for company member routes (any company role).
 */
export function CompanyRoute({ children }: { children?: React.ReactNode }) {
  return (
    <ProtectedRoute
      allowedRoles={[UserRole.CompanyAdmin, UserRole.CompanyUser, UserRole.CompanyViewer]}
    >
      {children}
    </ProtectedRoute>
  );
}
